<template>
    <div class="single-diamond-configurator container">
        <diamond-image
            v-if="selectedDiamond"
            :diamond="selectedDiamond"
            class="image"
        />
        <no-diamond-selected v-else class="image-empty"/>

        <div v-if="selectedDiamond" class="properties">
            <selected-diamond-gia-certificate/>
            <diamond-properties :diamond="selectedDiamond"/>
            <selected-diamond-gia-certificate-link/>
        </div>

        <div class="selection">
            <h1 class="single-diamond-configurator__heading">
                {{ t("singleDiamondsConfigurator.heading") }}
            </h1>
            <diamond-shape-select/>
            <div>
                <carat-select style="margin-top: 0" />
                <price-select/>
                <clarity-select/>
            </div>
            <diamond-table/>
            <single-diamond-selection-summary v-if="selectedDiamond"/>
            <buy-button v-if="selectedDiamond" class="buy-button buy-button__local"/>
            <teleport to="body">
                <buy-button v-if="selectedDiamond" class="buy-button buy-button__teleported"/>
            </teleport>
        </div>

        <div class="diagrams">
            <diamond-clarity-diagram/>
            <diamond-color-diagram/>
        </div>

        <div v-if="selectedDiamond" class="comparison">
            <h2 class="single-diamond-configurator__heading">
                {{ t("singleDiamondsConfigurator.compareHeading") }}
            </h2>
            <comparable-diamonds/>
        </div>
    </div>
</template>

<script lang="ts">
import {computed, defineComponent} from "vue";
import CaratSelect from "@/components/CaratSlect.vue";
import PriceSelect from "@/components/PriceSelect.vue";
import ClaritySelect from "@/components/ClaritySelect.vue";
import DiamondShapeSelect from "@/components/DiamandShapeSelect.vue";
import DiamondTable from "@/components/DiamondTable.vue";
import SingleDiamondSelectionSummary from "@/components/SingleDiamondSelectionSummary.vue";
import NoDiamondSelected from "@/components/NoDiamondSelected.vue";
import useStore from "@/composables/useStore";
import ComparableDiamonds from "@/components/ComparableDiamonds.vue";
import DiamondClarityDiagram from "@/components/DiamondClarityDiagram.vue";
import DiamondColorDiagram from "@/components/DiamondColorDiagram.vue";
import DiamondProperties from "@/components/DiamondProperties.vue";
import DiamondImage from "@/components/DiamondImage.vue";
import SelectedDiamondGiaCertificate from "@/components/SelectedDiamondGiaCertificate.vue";
import SelectedDiamondGiaCertificateLink from "@/components/SelectedDiamondGiaCertificateLink.vue";
import BuyButton from "@/components/single-diamond-configurator/BuyButton.vue";
import {useI18n} from "vue-i18n";

export default defineComponent({
    name: "SingleDiamond.vue",
    components: {
        BuyButton,
        SelectedDiamondGiaCertificate,
        SelectedDiamondGiaCertificateLink,
        DiamondImage,
        DiamondProperties,
        DiamondColorDiagram,
        DiamondClarityDiagram,
        ComparableDiamonds,
        NoDiamondSelected,
        SingleDiamondSelectionSummary,
        DiamondTable,
        DiamondShapeSelect,
        ClaritySelect,
        PriceSelect,
        CaratSelect
    },
    setup() {
        const store = useStore();
        const selectedDiamond = computed(
            () => store.state.userSelection.diamond
        );

        return {
            ...useI18n(),
            selectedDiamond
        };
    }
});
</script>

<style src="@/styles/app.css"/>

<style>
.single-diamond-configurator {
    font-family: apparat, sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    color: #111111;
    max-width: 100vw;
    overflow: hidden;
}

.container {
    display: grid;
    grid-auto-columns: 1fr;
    grid-template-columns: 1fr;
    grid-template-rows: min-content min-content min-content min-content min-content;
    gap: 3em;
    grid-template-areas:
        "selection"
        "image"
        "properties"
        "diagrams"
        "comparison";
}

.diagrams {
    grid-area: diagrams;
}

.comparison {
    grid-area: comparison;
}

.image {
    grid-area: image;
}

.properties {
    grid-area: properties;
}

.selection {
    grid-area: selection;
}

.selection {
    display: grid;
    grid-auto-rows: min-content;
    gap: 2em 0;
}

.comparison {
    min-width: 0;
}

h1.single-diamond-configurator__heading {
    margin: 0;
    font-family: garamond-premier-pro-display, "Crimson Text", sans-serif;
    font-style: normal;
    font-weight: 500;
    font-size: 32px;
    line-height: 36px;
    color: #111111;
}

h2.single-diamond-configurator__heading {
    margin: 0;
    margin-bottom: 0.375rem;
    font-style: normal;
    font-weight: 600;
    font-size: 16px;
    line-height: 28px;
    text-transform: uppercase;
    color: #111111;
}

.buy-button {
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100vw;
}

.nodiaimg {
    width: 100%;
    padding: 0.5rem;
}

.dim__iframe {
    width: 100%;
    height: 440px;
}

.buy-button__teleported {
    display: inline;
}
.buy-button__local {
    display: none;
}

@media (min-width: 1280px) {
    .container {
        display: grid;
        grid-template-columns: 1fr 1fr minmax(calc(400px + 5em), 1fr);
        grid-template-rows: 1fr min-content 1fr;
        gap: 3em 2em;
        grid-template-areas:
            "image properties selection"
            "diagrams diagrams selection"
            "comparison comparison selection";
    }

    .diagrams {
        display: grid;
        grid-template-columns: 1fr 1fr;
        grid-template-rows: 1fr;
    }

    .selection {
        margin-left: 5em;
    }

    .image-empty {
        grid-area: 1 / 1 / 1 / 3;
    }

    .buy-button {
        position: initial;
        bottom: initial;
        left: initial;
        width: initial;
    }

    .dim__iframe {
        width: auto;
        height: 400px;
    }

    .nodiaimg {
        width: 400px;
    }

    .buy-button__teleported {
        display: none;
    }
    .buy-button__local {
        display: inline;
    }
}
</style>
